import { RemoteData } from '@passionware/monads';
import { ReadWriteApiStore } from 'v5/platform/react/readWriteApiStore';
import type { ReadWriteApi } from 'v5/platform/ts/readWriteApi';
import { ProductionSearchQuery } from 'v5/api/production/production.api';
import { ResourceSearchQuery } from 'v5/api/resource/resource.api';
import type { TileSize } from 'v5/patterns/entity-list/tileSize';
import type { DashboardView } from 'v5/core/routing/dashboardRouting';

/**
 * Keyboard shortcut configuration for creating different entities
 */
export interface ShortcutMapping {
  create: {
    production: string;
    shortlist: string;
    booking: string;
    callsheet: string;
    callout: string;
    resource: string;
  };
}

export interface EntityListLayoutPreference {
  viewMode: 'grid' | 'list';
  tileSize: TileSize;
}

/** How the productions list budgeting column visualizes totals. */
export type ProductionListBudgetChartMode = 'vsPlanned' | 'vsBudget';

/** Productions list toolbar options (column visibility, etc.). */
export interface ProductionsListViewPreference {
  showResourcingBudgetColumn: boolean;
  budgetChartMode: ProductionListBudgetChartMode;
}

/** Booking confirmation template editor layout (settings page). */
export type BookingConfirmationTemplateEditorViewMode =
  | 'primary'
  | 'secondary'
  | 'split';

export interface BookingConfirmationTemplateEditorPreference {
  viewMode: BookingConfirmationTemplateEditorViewMode;
}

/** Which axis is in the feature-flag catalog sidebar. */
export type FeatureFlagManagerBrowseBy = 'feature' | 'workspace';

/** Feature flag manager dialog (ctrl+shift+l). */
export interface FeatureFlagManagerPreference {
  search: string;
  starredFeatureIds: string[];
  browseBy: FeatureFlagManagerBrowseBy;
}

/** Whose people an Add resources dialog links: an agent's or an organisation's. */
export type ContactPeopleRelation = 'agent' | 'organisation';

/**
 * UserPreferencesService - Manages user preferences including keyboard shortcuts and general preferences
 */
export interface UserPreferencesService {
  /**
   * Get keyboard shortcut mappings
   */
  useShortcuts: () => RemoteData<ShortcutMapping>;

  /**
   * Get persisted entity list layout for a given key (e.g. "resources"). Returns undefined when not set.
   */
  forEntityListLayout: (
    persistenceKey?: string
  ) => ReadWriteApiStore<EntityListLayoutPreference>;

  /**
   * Ordered visible table column ids for a list scope (e.g. "resource" or "resource:42").
   * Undefined means no user override — consumer applies catalog default.
   */
  forEntityTableColumns: (
    scopeKey: string
  ) => ReadWriteApiStore<string[] | undefined>;

  /**
   * A form group folded away by the user (a field group), under a key the
   * caller composes (`<workspace>/<resource type>/<group>`). `false` when
   * never folded.
   */
  forCollapsedFormGroup: (key: string) => ReadWriteApiStore<boolean>;

  /**
   * The resource types last chosen in the Type filter when adding an agent's or an
   * organisation's people. Undefined when never chosen: the dialog applies its default.
   */
  forContactPeopleTypeFilter: (
    relation: ContactPeopleRelation
  ) => ReadWriteApiStore<number[] | undefined>;

  /**
   * Productions list view options (e.g. optional resourcing budget column).
   * Global across all production list routes.
   */
  productionsListView: ReadWriteApiStore<ProductionsListViewPreference>;

  /**
   * Booking confirmation template editor: editor / preview / split layout.
   */
  bookingConfirmationTemplateEditor: ReadWriteApiStore<BookingConfirmationTemplateEditorPreference>;

  /**
   * Persisted production query used by the Create Callsheet dialog production picker.
   */
  callsheetsProductionQuery: ReadWriteApiStore<ProductionSearchQuery>;

  /**
   * Persisted resource query used by resourcing assign-resource dialog.
   */
  resourcingAssignResourceQuery?: ReadWriteApiStore<ResourceSearchQuery>;

  /** Grid or List on shortlist boards: mine only; the card size is the client's, on the shortlist. */
  shortlistBoardView: ReadWriteApiStore<ShortlistBoardViewPreference>;

  /** The resource picker's column covers the page it adds to (`SplitViewColumn.Maximize`). */
  resourcePickerMaximized: ReadWriteApiStore<boolean>;

  /** The resource picker's column width beside the page, in px; `null` until the user drags it. */
  resourcePickerWidth: ReadWriteApiStore<number | null>;

  /** The pinboard's tile size (its Size menu), the same on every resource. */
  pinboardTileSize: ReadWriteApiStore<TileSize>;

  /** What each result of a resource picker shows, mine only, the same wherever I add resources. */
  resourcePickerResultFields?: ReadWriteApiStore<ResourcePickerResultFields>;

  /** Documents' and production files' tile size (their Size menu), the same on every entity. */
  filesPanelTileSize: ReadWriteApiStore<TileSize>;

  /**
   * Feature flag manager dialog: search box and starred flag ids.
   */
  featureFlagManager: ReadWriteApiStore<FeatureFlagManagerPreference>;

  /**
   * The dashboard layout I picked, or `'auto'` once I skipped the question (the
   * page then picks by scope). `null` until I answered either way.
   */
  dashboardView: ReadWriteApiStore<DashboardViewPreference>;
}

export type DashboardViewPreference = DashboardView | 'auto' | null;

export interface WithUserPreferencesService {
  userPreferencesService: UserPreferencesService;
}

/** The pinboard's tiles as v1 showed pins: two to a row in a contact page's main column. */
export const DEFAULT_PINBOARD_TILE_SIZE: TileSize = 'extraLarge';

export interface ResourcePickerResultFields {
  /** Resource field keys on the second line, in order (`role`, `rates`, `custom_<id>`…). */
  line: string[];
  /**
   * What a row shows on its right: list fields as badges (`locations`, `tags`, `groups`) and
   * `pictures` (a strip in a row, a filmstrip on a tile).
   */
  right: string[];
}

/** What a result shows before anyone picks: role · rate, the tags and the pictures. */
export const DEFAULT_RESOURCE_PICKER_RESULT_FIELDS: ResourcePickerResultFields =
  { line: ['role', 'rates'], right: ['tags', 'pictures'] };

/** The closest to the small thumbnails files showed before the Size menu. */
export const DEFAULT_FILES_PANEL_TILE_SIZE: TileSize = 'small';

export interface ShortlistBoardViewPreference {
  layout: 'GRID' | 'LIST';
}

export const DEFAULT_SHORTLIST_BOARD_VIEW: ShortlistBoardViewPreference = {
  layout: 'GRID'
};

/** Where the shortlist board's preferences are kept; the host wires it (`.connected`). */
export interface ShortlistBoardPreferenceApis {
  shortlistBoardView: ReadWriteApi<ShortlistBoardViewPreference>;
  resourcePickerMaximized: ReadWriteApi<boolean>;
  resourcePickerWidth: ReadWriteApi<number | null>;
}
